import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SpinButton from 'Components/Buttons/Button/index.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';

// What the API accepts: 2 MB, JPEG or PNG only, and it must decode
// (DeviceVerificationReadingImageUploader). Everything here leaves as a 1600px JPEG, from the
// camera and from a chosen file alike, which lands inside all three.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;

// What a person is allowed to pick from filesystem. The crop shrinks whatever comes in.
// File size says little about how many pixels are inside, but it is all there is to go on
// before decoding, and it turns a file chosen by mistake into a sentence rather than a frozen tab.
const MAX_UPLOAD_BYTES = 3 * 1024 * 1024;

// The same two formats the API takes. file.type comes from the file's extension, not its content,
// so this decides what may be picked; decodeImage decides whether it really is a picture.
const UPLOAD_TYPES = ['image/jpeg', 'image/png'];

const UPLOAD_ERRORS = {
    invalidImageType: 'Only JPEG and PNG photos can be uploaded.',
    tooLarge: `That photo is larger than ${MAX_UPLOAD_BYTES / 1024 / 1024} MB. Choose a smaller one, or take the photo with the camera.`,
    undecodable: 'That file could not be opened as an image.',
    unreadable: 'That file could not be read.'
};

// getUserMedia reports what went wrong in err.name, and each one asks something different of
// whoever is standing in front of the meter. A refusal reads the same whether it came from the
// site's Permissions-Policy, from the browser or from the operating system, so the wording has to
// cover all three -- and a block answered once is remembered and never asked again, which is the
// part people get stuck on.
const CAMERA_ERRORS = {
    NotAllowedError: 'The camera was blocked for this page. Allow it for this site in the browser settings: an answer given once is remembered, and the browser will not ask a second time.',
    NotFoundError: 'This device has no camera to take the photo with.',
    NotReadableError: 'Another application is already using the camera. Close it and try again.',
    OverconstrainedError: 'No camera on this device can take this photo.'
};
const DEFAULT_CAMERA_ERROR = 'The camera could not be opened.';
const INSECURE_CONTEXT_ERROR = 'The camera is only available over a secure (HTTPS) connection.';

const readFileAsDataUrl = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('unreadable'));
    reader.readAsDataURL(file);
});

// A name ending in .jpg is not a JPEG: a renamed or damaged file would reach the cropper as a
// picture of nothing -- better to find that out here and say so.
const decodeImage = dataUrl => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('undecodable'));
    image.src = dataUrl;
});

const getCroppedImage = async (imageSrc, pixelCrop) => {
    const image = await decodeImage(imageSrc);
    const scale = Math.min(1, MAX_EDGE / Math.max(pixelCrop.width, pixelCrop.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(pixelCrop.width * scale);
    canvas.height = Math.round(pixelCrop.height * scale);
    canvas.getContext('2d').drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, canvas.width, canvas.height
    );
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};

const CameraModal = ({ open, onClose, onSave }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    // Every attempt takes a number, and stopStream bumps it, so a stream that arrives after the
    // modal has closed can tell that nobody is waiting for it any more.
    const attemptRef = useRef(0);
    const fileInputRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    // A camera that will not open leaves nothing to look at, so its reason takes the video's
    // place. A rejected file does not: the camera is still there, and the message sits beside it.
    const [cameraError, setCameraError] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const stopStream = useCallback(() => {
        attemptRef.current += 1;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const startCamera = useCallback(async () => {
        const attempt = attemptRef.current + 1;
        attemptRef.current = attempt;
        setCameraError(null);

        // Outside a secure context the browser exposes no mediaDevices at all.
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCameraError(INSECURE_CONTEXT_ERROR);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            if (attempt !== attemptRef.current) {
                // The modal closed while the browser was still asking. The cleanup that would have
                // stopped this stream has already run, so stop it here or the camera stays on.
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error('Camera access denied:', err);
            if (attempt === attemptRef.current) {
                setCameraError(CAMERA_ERRORS[err.name] || DEFAULT_CAMERA_ERROR);
            }
        }
    }, []);

    useEffect(() => {
        if (open && !capturedImage) {
            startCamera();
        }
        return () => stopStream();
    }, [open, capturedImage, startCamera, stopStream]);

    const handleChooseFile = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChosen = useCallback(async event => {
        const file = event.target.files?.[0];
        // Clear it or choosing the same file twice in a row fires no change at all.
        event.target.value = '';
        if (!file) {
            return;
        }

        setUploadError(null);
        if (!UPLOAD_TYPES.includes(file.type)) {
            setUploadError(UPLOAD_ERRORS.invalidImageType);
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setUploadError(UPLOAD_ERRORS.tooLarge);
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            await decodeImage(dataUrl);
            // The picture is the camera's output from here on: same crop, same size, same JPEG.
            stopStream();
            setCameraError(null);
            setCapturedImage(dataUrl);
        } catch (err) {
            console.error('Photo upload failed:', err);
            setUploadError(UPLOAD_ERRORS[err.message] || UPLOAD_ERRORS.unreadable);
        }
    }, [stopStream]);

    const handleCapture = useCallback(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        // Native resolution: the crop source; it never leaves the browser.
        setCapturedImage(canvas.toDataURL('image/jpeg', 0.92));
        setUploadError(null);
        stopStream();
    }, [stopStream]);

    const handleRetake = useCallback(() => {
        setCapturedImage(null);
        setUploadError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!capturedImage || !croppedAreaPixels) {
            return;
        }
        let cropped;
        try {
            cropped = await getCroppedImage(capturedImage, croppedAreaPixels);
        } catch (err) {
            console.error('Cropping the photo failed:', err);
            setUploadError(UPLOAD_ERRORS.undecodable);
            return;
        }
        onSave(cropped);
        setCapturedImage(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
    }, [capturedImage, croppedAreaPixels, onSave]);

    const handleCancel = useCallback(() => {
        stopStream();
        setCapturedImage(null);
        setCameraError(null);
        setUploadError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        onClose();
    }, [stopStream, onClose]);

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    return (
        <Dialog fullWidth maxWidth="md" onClose={handleCancel} open={open}>
            <DialogTitle>Capture Photo</DialogTitle>
            <DialogContent sx={{ overflow: 'visible', padding: 0 }}>
                {uploadError && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 24px 0' }}>
                        <p style={{ color: '#b00020', margin: 0, maxWidth: 460, textAlign: 'center' }}>{uploadError}</p>
                    </div>
                )}
                {cameraError ? (
                    <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 200, padding: 24 }}>
                        <p style={{ color: '#b00020', margin: 0, maxWidth: 460, textAlign: 'center' }}>{cameraError}</p>
                    </div>
                ) : !capturedImage ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <video
                            autoPlay
                            playsInline
                            ref={videoRef}
                            style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 4 }}
                        />
                    </div>
                ) : (
                    <div style={{ position: 'relative', width: '100%', height: 400 }}>
                        <Cropper
                            aspect={4 / 3}
                            crop={crop}
                            image={capturedImage}
                            maxZoom={5}
                            minZoom={0.5}
                            objectFit="contain"
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            restrictPosition={false}
                            showGrid
                            style={{
                                containerStyle: { background: '#333' },
                                cropAreaStyle: { border: '2px solid #fff', color: 'rgba(0, 0, 0, 0.5)' }
                            }}
                            zoom={zoom}
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <input
                    accept={UPLOAD_TYPES.join(',')}
                    onChange={handleFileChosen}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    type="file"
                />
                <Button color="primary" onClick={handleCancel}>Cancel</Button>
                {!capturedImage && (
                    <Button color="primary" onClick={handleChooseFile}>Upload</Button>
                )}
                {cameraError ? (
                    <SpinButton label="Try again" onClick={startCamera} size="small"/>
                ) : !capturedImage ? (
                    <SpinButton label="Capture" onClick={handleCapture} size="small"/>
                ) : (
                    <>
                        <Button color="primary" onClick={handleRetake}>Retake</Button>
                        <SpinButton label="Save" onClick={handleSave} size="small"/>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CameraModal;
